import { useState } from 'react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ChevronRight, Copy, ImageIcon } from 'lucide-react'
import { useImages, type ImagePageData } from '../hooks/useCrawls'

interface ImageData {
  src: string
  alt: string | null
  width: string | null
  height: string | null
  loading: string | null
}

function StatCard({ label, value, total, variant = 'neutral' }: {
  label: string
  value: number
  total: number
  variant?: 'neutral' | 'warning' | 'info'
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  const color = variant === 'warning'
    ? 'text-yellow-600 dark:text-yellow-400'
    : variant === 'info'
      ? 'text-blue-600 dark:text-blue-400'
      : 'text-foreground'

  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold tabular-nums mt-1 ${color}`}>
          {value.toLocaleString()}
        </p>
        {total > 0 && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {pct}% of {total.toLocaleString()} total images
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function IssueBar({ label, count, total }: { label: string; count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0
  const barColor = pct > 20 ? 'bg-red-500/80' : pct > 5 ? 'bg-yellow-500/80' : 'bg-green-500/80'

  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-40 shrink-0 text-muted-foreground">{label}</span>
      <div className="flex-1 h-5 rounded-sm bg-muted overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${label}: ${count} of ${total}`}>
        {pct > 0 && (
          <div
            className={`h-full rounded-sm transition-all ${barColor}`}
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        )}
      </div>
      <span className="w-16 text-right tabular-nums font-medium">
        {count.toLocaleString()}
      </span>
      <span className="w-12 text-right tabular-nums text-muted-foreground">{pct}%</span>
    </div>
  )
}

function ExpandablePageRow({ page }: { page: ImagePageData }) {
  const [expanded, setExpanded] = useState(false)

  let images: ImageData[] = []
  try {
    if (page.images_data) {
      images = JSON.parse(page.images_data) as ImageData[]
    }
  } catch { /* ignore parse errors */ }

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell className="w-6">
          <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </TableCell>
        <TableCell className="min-w-0">
          <span className="truncate block text-sm" title={page.url}>
            {page.url.replace(/^https?:\/\/[^/]+/, '')}
          </span>
        </TableCell>
        <TableCell className="text-center tabular-nums">{page.images_count}</TableCell>
        <TableCell className="text-center">
          {page.images_missing_alt > 0 ? (
            <Badge variant="outline" className="text-yellow-600 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700">{page.images_missing_alt}</Badge>
          ) : (
            <span className="text-muted-foreground">0</span>
          )}
        </TableCell>
        <TableCell className="text-center">
          {page.images_missing_dimensions > 0 ? (
            <Badge variant="outline" className="text-yellow-600 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700">{page.images_missing_dimensions}</Badge>
          ) : (
            <span className="text-muted-foreground">0</span>
          )}
        </TableCell>
        <TableCell className="text-center">
          {page.images_no_lazy_loading > 0 ? (
            <Badge variant="outline" className="text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-700">{page.images_no_lazy_loading}</Badge>
          ) : (
            <span className="text-muted-foreground">0</span>
          )}
        </TableCell>
      </TableRow>
      {expanded && images.length > 0 && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/30 p-0">
            <div className="px-8 py-3 space-y-2">
              {images.map((img, i) => {
                const missingAlt = img.alt === null || img.alt.trim() === ''
                const missingDims = img.width === null || img.height === null
                const notLazy = img.loading !== 'lazy'
                return (
                  <div key={i} className={`flex items-start gap-3 text-xs py-1.5 ${i > 0 ? 'border-t border-border/50' : ''}`}>
                    <span className="shrink-0 w-5 text-muted-foreground tabular-nums">{i + 1}</span>
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <p className="truncate text-muted-foreground" title={img.src}>
                        {img.src}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {missingAlt ? (
                          <Badge variant="outline" className="text-[10px] py-0 text-yellow-600 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700">No alt</Badge>
                        ) : (
                          <span className="text-muted-foreground truncate max-w-48" title={img.alt || ''}>
                            alt: {img.alt}
                          </span>
                        )}
                        {missingDims && (
                          <Badge variant="outline" className="text-[10px] py-0 text-yellow-600 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700">No dimensions</Badge>
                        )}
                        {!missingDims && (
                          <span className="text-muted-foreground">{img.width}x{img.height}</span>
                        )}
                        {notLazy && (
                          <Badge variant="outline" className="text-[10px] py-0 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-700">No lazy load</Badge>
                        )}
                        {img.loading === 'lazy' && (
                          <span className="text-muted-foreground">lazy</span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

type ImageFilter = 'all' | 'missing_alt' | 'missing_dims' | 'no_lazy'

const IMAGE_FILTERS: { label: string; value: ImageFilter }[] = [
  { label: 'All Pages', value: 'all' },
  { label: 'Missing Alt Text', value: 'missing_alt' },
  { label: 'Missing Dimensions', value: 'missing_dims' },
  { label: 'No Lazy Loading', value: 'no_lazy' },
]

function filterPages(pages: ImagePageData[], filter: ImageFilter): ImagePageData[] {
  switch (filter) {
    case 'missing_alt': return pages.filter(p => p.images_missing_alt > 0)
    case 'missing_dims': return pages.filter(p => p.images_missing_dimensions > 0)
    case 'no_lazy': return pages.filter(p => p.images_no_lazy_loading > 0)
    default: return pages
  }
}

export function ImagesTab({ crawlId }: { crawlId: string }) {
  const { data, isLoading } = useImages(crawlId, true)
  const [activeFilter, setActiveFilter] = useState<ImageFilter>('all')

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-64" />
      </div>
    )
  }

  const { stats, pages } = data
  const totalImages = stats.total_images || 0
  const filteredPages = filterPages(pages, activeFilter)

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <StatCard label="Total Images" value={totalImages} total={0} />
        <StatCard label="Missing Alt Text" value={stats.missing_alt || 0} total={totalImages} variant="warning" />
        <StatCard label="Missing Dimensions" value={stats.missing_dimensions || 0} total={totalImages} variant="warning" />
        <StatCard label="No Lazy Loading" value={stats.no_lazy_loading || 0} total={totalImages} variant="info" />
      </div>

      {/* Issue breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Issue Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <IssueBar label="Missing alt text" count={stats.missing_alt || 0} total={totalImages} />
          <IssueBar label="Missing dimensions" count={stats.missing_dimensions || 0} total={totalImages} />
          <IssueBar label="No lazy loading" count={stats.no_lazy_loading || 0} total={totalImages} />
        </CardContent>
      </Card>

      {/* Filter pills + Copy URLs */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {IMAGE_FILTERS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setActiveFilter(opt.value)}
              className={`rounded-md border px-3 py-1 text-xs transition-colors ${
                activeFilter === opt.value
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border text-muted-foreground hover:bg-accent/50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={async () => {
            try {
              const urls = filteredPages.map(p => p.url).join('\n')
              if (!urls) { toast.info('No URLs to copy for this filter.'); return }
              await navigator.clipboard.writeText(urls)
              toast.success(`Copied ${filteredPages.length} URL${filteredPages.length === 1 ? '' : 's'} to clipboard.`)
            } catch (err) {
              toast.error(`Copy failed: ${err instanceof Error ? err.message : 'unknown error'}`)
            }
          }}
          disabled={filteredPages.length === 0}
          title={filteredPages.length > 0 ? `Copy all ${filteredPages.length} matching URLs` : 'No URLs to copy'}
          className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent/50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Copy className="h-3 w-3" />
          Copy URLs ({filteredPages.length})
        </button>
      </div>

      {/* Pages with images info */}
      <p className="text-sm text-muted-foreground">
        {activeFilter === 'all'
          ? pages.length < (stats.pages_with_images || 0)
            ? `${stats.pages_with_images || 0} of ${stats.total_pages || 0} pages contain images — showing top ${pages.length} by alt-issue priority`
            : `${stats.pages_with_images || 0} of ${stats.total_pages || 0} pages contain images (${filteredPages.length} shown)`
          : pages.length < (stats.pages_with_images || 0)
            ? `${filteredPages.length} pages with ${IMAGE_FILTERS.find(f => f.value === activeFilter)?.label.toLowerCase()} (analysed in top ${pages.length} of ${stats.pages_with_images || 0} image-containing pages)`
            : `${filteredPages.length} pages with ${IMAGE_FILTERS.find(f => f.value === activeFilter)?.label.toLowerCase()}`
        }
      </p>

      {/* Per-page table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-6" />
                <TableHead>URL</TableHead>
                <TableHead className="text-center w-20">Images</TableHead>
                <TableHead className="text-center w-24">No Alt</TableHead>
                <TableHead className="text-center w-24">No Dims</TableHead>
                <TableHead className="text-center w-24">No Lazy</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    {activeFilter === 'all' ? 'No pages with images found' : 'No pages match this filter'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPages.map((page) => (
                  <ExpandablePageRow key={page.url} page={page} />
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
